import { useEffect, useRef, useState } from 'react';

// A truncated table-cell/card value with a floating tooltip revealing the full text — shows
// up instantly on hover (desktop) or tap (isMobile), styled like KpiTooltip's light card
// (white surface, card border, soft shadow) rather than the OS-native `title` tooltip.
// Lives in its own file (not inlined in SnapshotScreen) so both SnapshotScreen and DrillVoids
// can use it without creating a circular import between them.
export const CELL_TOOLTIP_MAX_WIDTH = 280;

const TruncatedCell = ({ text, style, isMobile }: { text: any; style?: React.CSSProperties; isMobile?: boolean }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const ref = useRef<HTMLDivElement>(null);

    // Only bother measuring/showing it when the text is actually clipped. Returns whether
    // it opened, so handleClick can know not to leave `open` true when there's nothing to show.
    const measureAndOpen = () => {
        const el = ref.current;
        if (!el || el.scrollWidth <= el.clientWidth) return false;
        const r = el.getBoundingClientRect();
        const left = Math.max(8, Math.min(r.left, window.innerWidth - CELL_TOOLTIP_MAX_WIDTH - 8));
        setPos({ top: r.bottom + 6, left });
        return true;
    };

    const handleEnter = () => { if (!isMobile && measureAndOpen()) setOpen(true); };
    const handleLeave = () => { if (!isMobile) setOpen(false); };
    const handleClick = (e: React.MouseEvent) => {
        if (!isMobile) return;
        // Stop the tap from also bubbling to a parent row's own onClick (e.g. a whole-check
        // void group's expand/collapse toggle).
        e.stopPropagation();
        setOpen((wasOpen) => (wasOpen ? false : measureAndOpen()));
    };

    // Mobile: tapping elsewhere (or scrolling) closes it, same as KpiTooltip's isMobile mode.
    useEffect(() => {
        if (!isMobile || !open) return;
        const closeOnScroll = () => setOpen(false);
        const closeOnOutsideTap = (e: Event) => {
            if (ref.current?.contains(e.target as Node)) return;
            setOpen(false);
        };
        window.addEventListener('scroll', closeOnScroll, { capture: true, passive: true });
        document.addEventListener('mousedown', closeOnOutsideTap, true);
        document.addEventListener('touchstart', closeOnOutsideTap, true);
        return () => {
            window.removeEventListener('scroll', closeOnScroll, true);
            document.removeEventListener('mousedown', closeOnOutsideTap, true);
            document.removeEventListener('touchstart', closeOnOutsideTap, true);
        };
    }, [isMobile, open]);

    return (
        <div
            ref={ref}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onClick={handleClick}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, cursor: isMobile ? 'pointer' : undefined, ...style }}
        >
            {text}
            {open && (
                <div style={{
                    position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
                    width: 'max-content', maxWidth: CELL_TOOLTIP_MAX_WIDTH,
                    background: 'var(--surface, #fff)', border: '1px solid var(--card-border, #E2E8F0)',
                    borderRadius: 8, padding: '8px 10px', fontSize: 11, color: 'var(--t2, #6B7280)',
                    lineHeight: 1.5, whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'left',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.14)', pointerEvents: 'none',
                }}>
                    {text}
                </div>
            )}
        </div>
    );
};

export default TruncatedCell;
